import { useOptimisticMutation } from "../hooks/useOptimisticMutation";
import { upsertSportsSuspensions } from "@/api/sportsNew";
import { SportsNewQueryKeys } from "@/tanstackQuery/queries/sportsNew";
import { encodeSportFeedKey } from "@/utils/sportFeedKey";
import { applyOptimisticFeedWrites } from "@/utils/optimisticFeedSuspensions";
import type {
  UpsertSportSuspensionsParams,
  SportSuspensionsEditorResponse,
} from "@/types/ApiResponses/Sport";

export const useUpsertSportsSuspensions = (targetUsername?: string) => {
  const queryKey = [SportsNewQueryKeys.SportsSuspensions, targetUsername];

  return useOptimisticMutation<
    UpsertSportSuspensionsParams,
    SportSuspensionsEditorResponse
  >({
    queryKey,
    mutationFn: upsertSportsSuspensions,
    optimisticUpdate: (variable, oldData) => {
      if (!oldData) return oldData;

      const newSuspensions = { ...oldData.suspensions };
      // suspensions is optional now — a caller may send only feedSuspensions rows.
      for (const s of variable.suspensions ?? []) {
        newSuspensions[s.sportId] = { id: s.sportId, active: s.active };
      }

      // Mirror the per-feed rows too, so a feed switch flips immediately
      // instead of waiting for the round trip.
      const newFeedSuspensions = applyOptimisticFeedWrites(
        oldData.feedSuspensions,
        (variable.feedSuspensions ?? []).map((s) => ({
          key: encodeSportFeedKey(s.sportId, s.feedType),
          active: s.active,
        })),
        (variable.feedOverrides ?? []).map((o) => ({
          key: encodeSportFeedKey(o.sportId, o.feedType),
          open: o.open,
        }))
      );

      return {
        ...oldData,
        suspensions: newSuspensions,
        feedSuspensions: newFeedSuspensions,
      };
    },
    updateOptions: { exactQueryKey: true },
  });
};
